import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { toast } from "sonner";
import type { TakeoffOwnerType } from "./useDocumentAnnotations";

export interface TakeoffGroup {
  id: string;
  company_id: string;
  estimate_id: string | null;
  project_id: string | null;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
  is_archived: boolean;
  source_estimate_group_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface UseTakeoffGroupsOpts {
  estimateId?: string;
  projectId?: string;
}

export function useTakeoffGroups({ estimateId, projectId }: UseTakeoffGroupsOpts) {
  const ownerType: TakeoffOwnerType = estimateId ? "estimate" : "project";
  const ownerId = estimateId || projectId;
  const fkColumn = ownerType === "estimate" ? "estimate_id" : "project_id";

  return useQuery({
    queryKey: ["takeoff_groups", ownerType, ownerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("takeoff_groups")
        .select("*")
        .eq(fkColumn, ownerId!)
        .eq("is_archived", false)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TakeoffGroup[];
    },
    enabled: !!ownerId,
  });
}

export function useCreateTakeoffGroup() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();

  return useMutation({
    mutationFn: async (params: {
      ownerType: TakeoffOwnerType;
      ownerId: string;
      name: string;
      color?: string;
      description?: string;
    }) => {
      const insertObj: any = {
        company_id: activeCompanyId!,
        name: params.name,
        color: params.color || "#3B82F6",
        description: params.description || null,
        created_by: user!.id,
      };

      if (params.ownerType === "estimate") {
        insertObj.estimate_id = params.ownerId;
      } else {
        insertObj.project_id = params.ownerId;
      }

      const { data, error } = await supabase
        .from("takeoff_groups")
        .insert(insertObj)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as TakeoffGroup;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["takeoff_groups", vars.ownerType, vars.ownerId] });
      toast.success(`Group "${vars.name}" created`);
    },
    onError: (err: any) => toast.error(err.message),
  });
}

export function useUpdateTakeoffGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      ownerType: TakeoffOwnerType;
      ownerId: string;
      name?: string;
      color?: string;
      description?: string | null;
      sort_order?: number;
      is_archived?: boolean;
    }) => {
      const updates: Record<string, any> = { updated_at: new Date().toISOString() };
      if (params.name !== undefined) updates.name = params.name;
      if (params.color !== undefined) updates.color = params.color;
      if (params.description !== undefined) updates.description = params.description;
      if (params.sort_order !== undefined) updates.sort_order = params.sort_order;
      if (params.is_archived !== undefined) updates.is_archived = params.is_archived;

      const { error } = await supabase
        .from("takeoff_groups")
        .update(updates)
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["takeoff_groups", vars.ownerType, vars.ownerId] });
    },
    onError: (err: any) => toast.error(err.message),
  });
}

export function useDeleteTakeoffGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      ownerType: TakeoffOwnerType;
      ownerId: string;
    }) => {
      const { error } = await supabase
        .from("takeoff_groups")
        .delete()
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["takeoff_groups", vars.ownerType, vars.ownerId] });
      qc.invalidateQueries({ queryKey: ["annotation_counts"] });
      toast.success("Group deleted — annotations moved to Unassigned");
    },
    onError: (err: any) => toast.error(err.message),
  });
}

export function useAssignAnnotationsToGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      annotationIds: string[];
      groupId: string | null;
    }) => {
      const { error } = await supabase
        .from("document_annotations")
        .update({ takeoff_group_id: params.groupId })
        .in("id", params.annotationIds);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document_annotations"] });
      qc.invalidateQueries({ queryKey: ["annotation_counts"] });
      qc.invalidateQueries({ queryKey: ["takeoff_groups"] });
    },
    onError: (err: any) => toast.error(err.message),
  });
}
